import { useLingui } from '@lingui/react/macro';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { defineBlockComponent, type BlockComponentProps } from '@/shared/page';
import { useMediaQuery } from '@/shared/mobile';
import { cn } from '@/shared/ui/cn';

import { readPath, resolvePatternText, textSchema, type PatternText } from './text';
import { formatScalar } from './values';
import { NotAvailable } from './value';

/** Formatos do KPI: os mesmos presets escalares da coluna. */
export const KPI_FORMATS = ['money', 'number', 'percent'] as const;
export type KpiFormat = (typeof KPI_FORMATS)[number];

const COUNT_MS = 300;

/**
 * Gesto `count` (U38): o número anima até o valor novo, em até 300 ms, só entre inteiros (centavos
 * ou contagem). Com `prefers-reduced-motion`, vai direto.
 */
export function useCountUp(target: number | undefined): number | undefined {
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [shown, setShown] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    if (target === undefined || from.current === undefined || reduced || !Number.isSafeInteger(target)) {
      from.current = target;
      setShown(target);
      return;
    }
    const start = from.current;
    const began = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const progress = Math.min(1, (now - began) / COUNT_MS);
      const eased = 1 - (1 - progress) ** 3;
      setShown(Math.round(start + (target - start) * eased));
      if (progress < 1) frame = requestAnimationFrame(step);
      else from.current = target;
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, reduced]);
  return shown;
}

export interface KpiCardProps {
  readonly label: PatternText;
  readonly value: unknown;
  readonly format: KpiFormat;
  /** Dinheiro: `cents` (padrão) ou `decimal`. */
  readonly unit?: 'cents' | 'decimal';
  readonly hint?: PatternText | undefined;
  readonly presentation?: 'web' | 'mobile';
}

function useKpiText(value: unknown, format: KpiFormat, unit: 'cents' | 'decimal' | undefined) {
  const { i18n } = useLingui();
  const countable = (format === 'number' || (format === 'money' && unit !== 'decimal')) && typeof value === 'number' && Number.isSafeInteger(value);
  const counted = useCountUp(countable ? value : undefined);
  const shown = countable ? counted : value;
  return formatScalar(format, shown, i18n.locale, unit ? { unit } : {});
}

/** Um número com rótulo e dica (P23/09 §10.2). Dinheiro e contagem em DM Mono tabular (U39 §11). */
export function KpiCard({ label, value, format, unit, hint, presentation = 'web' }: KpiCardProps) {
  const { i18n } = useLingui();
  const text = useKpiText(value, format, unit);
  const labelText = resolvePatternText(i18n, label);
  const hintText = hint === undefined ? undefined : resolvePatternText(i18n, hint);
  if (presentation === 'mobile') {
    return (
      <div data-slot="kpi-card" className="flex flex-col gap-1 rounded-2xl border border-border bg-card px-4 py-3.5">
        <p className="text-sm text-muted-foreground">{labelText}</p>
        <p className="font-mono text-3xl font-semibold tabular-nums">{text ?? <NotAvailable />}</p>
        {hintText ? <p className="text-sm text-muted-foreground">{hintText}</p> : null}
      </div>
    );
  }
  return (
    <div data-slot="kpi-card" className="flex flex-col gap-1 @min-[721px]/block:flex-row @min-[721px]/block:items-baseline @min-[721px]/block:gap-4">
      <p className="text-xs text-muted-foreground">{labelText}</p>
      <p className={cn('font-mono text-2xl font-semibold tabular-nums @min-[361px]/block:text-3xl')}>{text ?? <NotAvailable />}</p>
      {hintText ? <p className="text-xs text-muted-foreground">{hintText}</p> : null}
    </div>
  );
}

/** Props do `KpiCard` como genérico de bloco: o campo do dado por caminho e o formato por nome. */
export const kpiCardBlockProps = z.object({
  label: textSchema,
  field: z.string().min(1),
  format: z.enum(KPI_FORMATS),
  unit: z.enum(['cents', 'decimal']).optional(),
  hint: textSchema.optional(),
});

export type KpiCardBlockProps = z.infer<typeof kpiCardBlockProps>;
type View = BlockComponentProps<KpiCardBlockProps, object>;

function KpiCardBlockWeb({ props, data }: View) {
  return <KpiCard label={props.label} value={readPath(data, props.field)} format={props.format} {...(props.unit ? { unit: props.unit } : {})} hint={props.hint} />;
}

function KpiCardBlockMobile({ props, data }: View) {
  return <KpiCard label={props.label} value={readPath(data, props.field)} format={props.format} {...(props.unit ? { unit: props.unit } : {})} hint={props.hint} presentation="mobile" />;
}

/** `KpiCard` como genérico do catálogo, sobre uma fonte `record` ou `scalar`. */
export const KpiCardBlock = defineBlockComponent<KpiCardBlockProps, object>({
  id: 'KpiCard',
  description: 'Single figure with label and hint, formatted as money, number or percent, animating to new values.',
  props: kpiCardBlockProps,
  render: { web: KpiCardBlockWeb, mobile: KpiCardBlockMobile },
});
